/** Owner keys: what is issued, when it was last used, and revocation. */
import { useState } from 'react';
import { useQuery } from '../transport/hooks.ts';
import { request } from '../transport/socket.ts';
import type { DeviceSummary } from '../../shared/protocol/methods.ts';
import { TwoFactor } from '../components/TwoFactor.tsx';
import { Segmented } from '../components/Segmented.tsx';
import { useParam } from '../router.tsx';

function when(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
}


/**
 * История входов и выдач.
 *
 * Отдельной вкладкой, потому что отвечает на другой вопрос, чем список ключей.
 * Список говорит «кто может войти», история — «кто входил», и второе нужно
 * ровно тогда, когда первое вызывает подозрение.
 *
 * Берём из общей ленты событий, а не заводим свой журнал: события уже пишутся
 * и уже переживают перезапуск, а второй журнал того же — это второе место, где
 * правда может разойтись.
 */
function AuthHistory(): React.JSX.Element {
  const { data, loading } = useQuery<{
    items: { ts: string; level: string; type: string; msg: string }[];
  }>('events.list', { limit: 100 });

  if (loading && !data) return <p className="muted">Загружаю…</p>;

  const ИНТЕРЕСНОЕ = /^(auth\.|device\.|mcp\.key)/;
  const rows = (data?.items ?? []).filter((e) => ИНТЕРЕСНОЕ.test(e.type));

  if (rows.length === 0) {
    return <p className="muted">Записей о входах пока нет.</p>;
  }

  return (
    <table className="table">
      <thead>
        <tr><th>Когда</th><th>Что</th><th /></tr>
      </thead>
      <tbody>
        {rows.map((e, i) => (
          <tr key={`${e.ts}-${i}`} className={e.level === 'warn' ? 'row--warn' : undefined}>
            <td className="mono">{new Date(e.ts).toLocaleString('ru-RU')}</td>
            <td className="mono muted">{e.type}</td>
            <td>{e.msg}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Devices(): React.JSX.Element {
  const { data, error, loading, reload } = useQuery<{ items: DeviceSummary[] }>('devices.list');
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  /*
   * Подвкладки, а не одна простыня.
   *
   * На экране сошлось три разных дела: ключи владельца, подключение помощников
   * и вход в консоль. Ими занимаются в разное время и по разным поводам — а
   * рядом друг с другом они мешают: чтобы отозвать ключ, надо проскроллить
   * мимо генератора QR, который сейчас не нужен.
   */
  const [tab, setTab] = useParam('section', 'keys', ['keys', 'history'] as const);

  async function onRevoke(kid: string): Promise<void> {
    setBusy(kid);
    setFailure(null);
    try {
      await request('device.revoke', { kid, reason: 'отозван из консоли' });
      reload();
    } catch (err) {
      setFailure((err as { msg?: string }).msg ?? 'не удалось отозвать ключ');
    } finally {
      setBusy(null);
    }
  }

  if (loading && !data) return <p className="muted">Загружаю список ключей…</p>;
  if (error) return <p className="alert alert--error">Ошибка: {error.msg}</p>;

  const items = data?.items ?? [];

  return (
    <section className="panel panel--fill">
      <header className="panel__head">
        <h2 className="panel__title">Ключи доступа</h2>
      </header>

      <div className="panel__tabs">
        <Segmented
          ariaLabel="разделы ключей"
          active={tab}
          onSelect={(id) => setTab(id as typeof tab)}
          items={[
            { id: 'keys', label: 'Ключи владельца', count: items.filter((d) => !d.revokedAt).length },
            { id: 'history', label: 'История входов' },
          ]}
        />
      </div>

      {tab === 'keys' && <TwoFactor />}

      <div className="panel__scroll">
      {failure && <p className="alert alert--error">{failure}</p>}
      {tab === 'keys' && (
        <>

      <table className="table">
        <thead>
          <tr>
            <th>Метка</th>
            <th>kid</th>
            <th>Выдан</th>
            <th>Активирован</th>
            <th>Последний вход</th>
            <th>Устройств</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((d) => (
            <tr key={d.kid} className={d.revokedAt ? 'row--revoked' : undefined}>
              <td>
                {d.label}
                {d.current && <span className="tag">этот браузер</span>}
              </td>
              <td className="mono">{d.kid}</td>
              <td>{when(d.createdAt)}</td>
              <td>{d.activatedAt ? when(d.activatedAt) : <span className="muted">не активирован</span>}</td>
              <td>{when(d.lastSeenAt)}</td>
              <td>{d.installCount}</td>
              <td>
                {d.revokedAt ? (
                  <span className="muted">отозван {when(d.revokedAt)}</span>
                ) : d.current ? (
                  <span
                    className="muted"
                    title={
                      'Отзыв этого ключа оборвал бы вашу же сессию. Если он ' +
                      'скомпрометирован — выпустите новый, войдите им и отзовите этот.'
                    }
                  >
                    ключ текущей сессии
                  </span>
                ) : (
                  <button
                    type="button"
                    className="button button--danger"
                    disabled={busy === d.kid}
                    onClick={() => void onRevoke(d.kid)}
                  >
                    {busy === d.kid ? '…' : 'Отозвать'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="muted">
        Отзыв срабатывает сразу: открытые сессии этого ключа разрываются за
        секунды и вернуться этим файлом уже нельзя. Два ключа отозвать отсюда
        не получится: тот, которым вы сейчас вошли, и последний оставшийся —
        оба случая заканчиваются потерей доступа к самой консоли. Аварийный
        выход — командная строка: <span className="mono">bun run revoke-key</span>.
        </p>
        </>
      )}

      {tab === 'history' && <AuthHistory />}
      </div>
    </section>
  );
}
